"use client";

/**
 * AdminFilterFields — 필터 필드 목록을 배열 하나로 선언하는 컴포넌트
 *
 * AdminFilter 안에 AdminFilterField + AdminCheckboxFilter/AdminSelect 를 필드마다
 * 반복해서 작성하는 대신, items 배열 하나로 선언하면 type 에 따라 자동으로 렌더링한다.
 * (AdminFormField 가 type 으로 입력 종류를 자동 생성하는 것과 같은 방식)
 *
 * 현재는 "checkbox"(다중 체크박스)와 "select"(단일 선택) 두 타입을 지원한다.
 *
 * @example
 * <AdminFilterFields
 *   items={[
 *     {
 *       type: "checkbox",
 *       label: "상태",
 *       options: kioskOptions.kioskStatus,
 *       value: filters.options?.status,
 *       onChange: (v) => setFiltersField("options", { ...filters.options, status: v }),
 *       loading: kioskOptionsLoading,
 *     },
 *     {
 *       type: "select",
 *       label: "파트너",
 *       options: partnerOptions,
 *       value: filters.options.partner_key,
 *       onChange: (v) => setFiltersField("options", { ...filters.options, partner_key: v }),
 *       placeholder: "전체",
 *       searchable: false,
 *       clearable: true,
 *     },
 *   ]}
 * />
 */
import { useId, type ReactNode } from "react";

import { cn } from "../../lib/utils";
import {
  AdminSelect,
  type AdminSelectOption,
  type AdminSelectProps,
} from "../select/admin-select";
import { AdminFilterField } from "./admin-filter";
import {
  AdminCheckboxFilter,
  type AdminCheckboxFilterOption,
} from "./admin-checkbox-filter";

export interface AdminFilterFieldsCheckboxItem {
  type: "checkbox";
  label?: ReactNode;
  options: AdminCheckboxFilterOption[];
  /** 선택된 값 배열 (기본 []) */
  value?: string[];
  onChange: (value: string[]) => void;
  /** 옵션 로딩 중 표시 (기본 false) */
  loading?: boolean;
  /** 로딩 스켈레톤 개수 (기본 3) */
  skeletonCount?: number;
  htmlFor?: string;
  className?: string;
}

export interface AdminFilterFieldsSelectItem {
  type: "select";
  label?: ReactNode;
  options: AdminSelectOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** 옵션 로딩 중 표시 (기본 false). 켜지면 select 대신 스켈레톤을 보여준다 */
  loading?: boolean;
  htmlFor?: string;
  className?: string;
  /** AdminSelect 에 추가로 전달할 속성 (searchable, clearable 등) */
  selectProps?: Omit<
    AdminSelectProps,
    "options" | "value" | "onChange" | "id" | "placeholder"
  >;
}

export type AdminFilterFieldsItem =
  | AdminFilterFieldsCheckboxItem
  | AdminFilterFieldsSelectItem;

export interface AdminFilterFieldsProps {
  items: AdminFilterFieldsItem[];
  className?: string;
}

export function AdminFilterFields({ items, className }: AdminFilterFieldsProps) {
  const autoId = useId();

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {items.map((item, index) => {
        const htmlFor = item.htmlFor ?? `${autoId}-${index}`;

        if (item.type === "checkbox") {
          return (
            <AdminCheckboxFilter
              key={index}
              label={item.label}
              options={item.options}
              value={item.value}
              onChange={item.onChange}
              loading={item.loading}
              skeletonCount={item.skeletonCount}
              htmlFor={htmlFor}
              className={item.className}
            />
          );
        }

        return (
          <AdminFilterField
            key={index}
            label={item.label}
            htmlFor={htmlFor}
            className={item.className}
          >
            {item.loading ? (
              <div className="h-9 w-full animate-pulse rounded-md bg-muted" />
            ) : (
              <AdminSelect
                id={htmlFor}
                options={item.options}
                value={item.value}
                onChange={item.onChange}
                placeholder={item.placeholder}
                {...item.selectProps}
              />
            )}
          </AdminFilterField>
        );
      })}
    </div>
  );
}
